import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PasswordResetConfirmPage = () => {
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/confirm`, {
        token,
        new_password: newPassword
      });
      
      toast.success("Password reset successfully!");
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      if (message.includes("expired")) {
        toast.error("Reset token has expired. Please request a new one.");
      } else if (message.includes("Invalid")) {
        toast.error("Invalid reset token. Please check and try again.");
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full border border-[#C9A962]/30 flex items-center justify-center">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-display font-medium text-gradient-luxury">OCULUS</h1>
              <p className="text-[#C9A962] text-xs tracking-[0.2em] font-elegant">ROYALE</p>
            </div>
          </div>
        </div>

        <div className="luxury-card p-8 md:p-10">
          <div className="mb-8">
            <h2 className="text-2xl font-display font-medium text-white mb-2">Create New Password</h2>
            <p className="text-[#9A9A9A] text-sm leading-relaxed">
              Enter the reset token from your email and choose a new password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="token" className="text-[#9A9A9A] text-xs uppercase tracking-wider">
                Reset Token
              </Label>
              <Input
                id="token"
                type="text"
                placeholder="Paste your reset token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12 font-mono text-sm"
                autoFocus
              />
              <p className="text-[#5A5A5A] text-xs mt-1">
                Check your email for the reset token
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-[#9A9A9A] text-xs uppercase tracking-wider">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#9A9A9A] transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#9A9A9A] text-xs uppercase tracking-wider">
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12"
              />
            </div>

            {/* Password requirements */}
            <div className="bg-[#1A1A1A] border border-[#252525] rounded-lg p-4">
              <p className="text-[#9A9A9A] text-xs mb-2 uppercase tracking-wider">Password Requirements:</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CheckCircle2 
                    className={`w-4 h-4 ${newPassword.length >= 8 ? 'text-[#4A8A4A]' : 'text-[#5A5A5A]'}`}
                  />
                  <span className="text-xs text-[#9A9A9A]">At least 8 characters</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 
                    className={`w-4 h-4 ${newPassword && confirmPassword && newPassword === confirmPassword ? 'text-[#4A8A4A]' : 'text-[#5A5A5A]'}`}
                  />
                  <span className="text-xs text-[#9A9A9A]">Passwords match</span>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-[#C9A962] to-[#A68B4B] hover:opacity-90 text-[#0A0A0A] font-semibold text-xs tracking-widest uppercase"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>

          <div className="luxury-divider my-8">or</div>

          <div className="text-center space-y-3">
            <p className="text-[#5A5A5A] text-sm">
              Didn&apos;t receive a token?{" "}
              <Link to="/password-reset" className="text-[#C9A962] hover:text-[#D4B978] transition-colors">
                Request New One
              </Link>
            </p>
            <p className="text-[#5A5A5A] text-sm">
              Remember your password?{" "}
              <Link to="/login" className="text-[#C9A962] hover:text-[#D4B978] transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirmPage;
